import React from "react";
import { Alert, Drawer, Flex, Space, Spin } from "antd";
import { Checkbox } from "@heroui/checkbox";
import { Button } from "@heroui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { GQL } from "@binale-tech/shared";
import { gql } from "@apollo/client";

import { ProductKeys } from "scripts/models/Product";
import { UserContext } from "scripts/context/UserProvider";
import { useGQLRetriever } from "scripts/graphql/gqlRetriever";
import { useGqlMutator } from "scripts/graphql/useGqlMutator";

type Props = {
    company?: GQL.ICompany;
    onHide: () => void;
};

const fragment = gql`
    fragment CompanyProductsData on Company {
        id
        name
        products {
            id
            productKey
        }
    }
`;
const query = gql`
    ${fragment}
    query companyWithProducts($id: ID!) {
        company(id: $id) {
            ...CompanyProductsData
        }
    }
`;
const mutation = gql`
    ${fragment}
    mutation companySetProducts($input: CompanySetProductsInput!) {
        companySetProducts(input: $input) {
            ...CompanyProductsData
        }
    }
`;

export const CompanyModules: React.FC<Props> = props => {
    const [loading, setLoading] = React.useState(false);
    const [products, setProducts] = React.useState<GQL.ICompanyProductInput[]>([]);
    const { isAdminWrite } = React.use(UserContext);
    const { company } = props;
    const name = company?.name;
    const companyId = company?.id;
    const intl = useIntl();
    const mutator = useGqlMutator();
    const retriever = useGQLRetriever<"company">();

    React.useEffect(() => {
        if (!companyId) {
            setProducts([]);
            return;
        }
        const getCompanyData = async () => {
            setLoading(true);
            const response = await retriever.query({ query, id: companyId });
            if (response.company) {
                setProducts(response.company.products.map(({ productKey }) => ({ productKey })));
            }
            setLoading(false);
        };
        getCompanyData();
    }, [retriever, companyId]);

    const onValueChange = (pk: GQL.IProductKey) => (isSelected: boolean) => {
        setProducts(prevState => {
            const keys = new Set(prevState.map(v => v.productKey));
            if (isSelected) {
                keys.add(pk);
            } else {
                keys.delete(pk);
            }
            return ProductKeys.all()
                .filter(k => keys.has(k))
                .map(
                    k =>
                        ({
                            productKey: k,
                        }) satisfies GQL.ICompanyProductInput
                );
        });
    };

    const handleClick = () => {
        setLoading(true);
        mutator
            .mutate<"companySetProducts", GQL.ICompanySetProductsInput>({
                mutation,
                input: { companyId, products },
            })
            .then(() => {
                setLoading(false);
                setTimeout(() => props.onHide(), 500);
            });
    };

    const values = ProductKeys.all().map(value => {
        return {
            value,
            label: intl.formatMessage({ id: "app.titles." + value }),
            selected: Boolean(products.find(p => p.productKey === value)),
            disabled: !isAdminWrite,
        };
    });

    const disabled = loading || !isAdminWrite;

    return (
        <Drawer
            width={400}
            title={
                <Flex justify={"space-between"} align={"center"} style={{ width: "100%" }}>
                    <span>
                        {name}: <FormattedMessage id="app.fields.products" />
                    </span>
                    <Button
                        size={"lg"}
                        color="primary"
                        isDisabled={!isAdminWrite}
                        isLoading={loading}
                        onPress={handleClick}
                    >
                        <FormattedMessage id="app.button.save" />
                    </Button>
                </Flex>
            }
            placement="right"
            open={Boolean(companyId)}
            onClose={props.onHide}
        >
            <Spin spinning={loading}>
                <Space direction={"vertical"}>
                    {!isAdminWrite && <Alert showIcon message="Only admin can set products" type="warning" />}
                    {values.map(value => (
                        <Checkbox
                            key={value.value}
                            size="lg"
                            isDisabled={disabled}
                            isSelected={value.selected}
                            onValueChange={onValueChange(value.value)}
                        >
                            {value.label}
                        </Checkbox>
                    ))}
                </Space>
            </Spin>
        </Drawer>
    );
};
